import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FilterDropdown from '../../components/common/FilterDropdown';
import { GenericView, SearchInput, ActionButtons } from '../../components/common/table';
import ApprovalWorkflowForm from './components/CreateTypeUnitForm';
import ViewWorkflow from './components/ViewTypeUnit';
import Alert from '../../components/Alert/Alert';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import { getTypeUnits, deleteTypeUnit, updateTypeUnit, createTypeUnit } from './api/typeUnitApi';

const CreateTypeUnit = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterData, setFilterData] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [typeUnits, setTypeUnits] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const showAlert = (type, message) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000); // Auto hide after 5 seconds
  };

  useEffect(() => {
    loadTypeUnits();
  }, []);

  const loadTypeUnits = async () => {
    setIsLoading(true);
    try {
      const response = await getTypeUnits();
      // Mapping: name -> typeUnitName
      const data = (Array.isArray(response) ? response : response.data || []).map(item => ({
        ...item,
        typeUnitName: item.name // tambahkan mapping ini
      }));
      setTypeUnits(data);
    } catch (error) {
      console.error('Error loading type units:', error);
      showAlert('danger', 'Failed to load type units.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilterData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFilterReset = () => {
    setFilterData({
      dateFrom: '',
      dateTo: '',
      client: '',
      project: '',
      status: ''
    });
  };

  const handleFilterApply = () => {
    // Add your filter logic here
  };

  const columns = [
    { key: 'typeUnitName', label: 'Type Unit Name' },
    { key: 'description', label: 'Description' }
  ];

  // Remove workflow-specific dummy data and logic

  const handleFormSubmit = async (formData) => {
    // Ubah payload agar field 'typeUnitName' menjadi 'name'
    const payload = {
      name: formData.typeUnitName,
      description: formData.description
    };
    try {
      if (selectedWorkflow) {
        await updateTypeUnit(selectedWorkflow.id, payload);
        showAlert('success', 'Type unit updated successfully!');
      } else {
        await createTypeUnit(payload);
        showAlert('success', 'Type unit created successfully!');
      }
      await loadTypeUnits();
      setIsModalOpen(false);
      setSelectedWorkflow(null);
    } catch (error) {
      console.error('Error saving type unit:', error);
      showAlert('danger', 'Failed to save type unit. Please check your input.');
    }
  };

  // Add these new handler functions
  const handleEdit = (row) => {
    setSelectedWorkflow(row);
    setIsModalOpen(true);
  };

  const handleDelete = async (row) => {
    setItemToDelete(row);
    setShowDeleteModal(true);
  };



  const handleView = (row) => {
    setSelectedWorkflow(row);
    setIsViewModalOpen(true);
  };

  return (
    <div className="container-xl">
      {/* Alert Component */}
      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}

      <div className="row g-2 align-items-center">
        <div className="col">
          <h3 className="page-title m-0">Type Unit Management</h3>
        </div>
        <div className="col-auto ms-auto d-print-none">
          <div className="btn-list">
            <button 
              className="btn btn-primary"
              onClick={() => setIsModalOpen(true)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-plus" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                <path d="M12 5l0 14"></path>
                <path d="M5 12l14 0"></path>
              </svg>
              Create
            </button>
          </div>
        </div>
      </div>

      <ApprovalWorkflowForm
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedWorkflow(null);
        }}
        onSubmit={handleFormSubmit}
        initialData={selectedWorkflow}
      />

      <ViewWorkflow
        isOpen={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setSelectedWorkflow(null);
        }}
        data={selectedWorkflow}
      />

      {/* Confirmation Modal for Delete */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setItemToDelete(null);
        }}
        onConfirm={async () => {
          if (!itemToDelete?.id) return;
          setIsLoading(true);
          try {
            await deleteTypeUnit(itemToDelete.id);
            showAlert('success', 'Type unit deleted successfully!');
            await loadTypeUnits();
          } catch (error) {
            console.error('Error deleting type unit:', error);
            showAlert('danger', 'Failed to delete type unit.');
          } finally {
            setIsLoading(false);
          }
          setShowDeleteModal(false);
          setItemToDelete(null);
        }}
        title="Delete Type Unit"
        message={`Are you sure you want to delete this? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />

      <div className="page-body">
        <div className="container-xl">
          <div className="col">
            <div className="card">
              <div className="card-header">
                <ul className="nav nav-tabs card-header-tabs" data-bs-toggle="tabs" role="tablist">
                  <li className="nav-item-tab" role="presentation">
                    <a href="#tabs-list-data" className="nav-link active" data-bs-toggle="tab" aria-selected="true" role="tab">List Data</a>
                  </li>
                </ul>
              </div>
              <div className="card-body">
                <div className="tab-content">
                  <div className="tab-pane active show" id="tabs-list-data" role="tabpanel">
                    <GenericView
                      data={typeUnits}
                      columns={columns}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onView={handleView}
                      isLoading={isLoading}
                      filterComponent={
                        <FilterDropdown
                          filterData={filterData}
                          onFilterChange={handleFilterChange}
                          onReset={handleFilterReset}
                          onApply={handleFilterApply}
                        />
                      }
                      searchComponent={
                        <SearchInput
                          value={searchQuery}
                          onChange={setSearchQuery}
                          placeholder="Search type units..."
                        />
                      }
                      actionButtons={
                        <ActionButtons
                          onExportExcel={() => {
                            showAlert('info', 'Export to Excel functionality coming soon');
                          }}
                          onExportPDF={() => {
                            showAlert('info', 'Export to PDF functionality coming soon');
                          }}
                        />
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateTypeUnit; 